import { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Badge,
  Button,
  Link,
  Modal,
  OutlineCard,
  Switch
} from '@morressier/alembic';
import { COOKIE_POLICY_URL, PRIVACY_POLICY_URL } from './config';
import { useCookieConsent } from './CookieConsentContext';
import { contactSupportMail } from 'src/utils/env';

const CookieConsentBanner = () => {
  const {
    preferences,
    shouldShowBanner,
    isPreferencesOpen,
    closePreferences,
    acceptAll,
    rejectAll,
    savePreferences
  } = useCookieConsent();
  const [draftPreferences, setDraftPreferences] = useState({
    analytics: preferences.analytics
  });

  useEffect(() => {
    setDraftPreferences({
      analytics: preferences.analytics
    });
  }, [preferences.analytics, isPreferencesOpen]);


  const isCookieSettingsModalOpen = isPreferencesOpen;
  const isConsentModalOpen = shouldShowBanner && !isCookieSettingsModalOpen;
  const isModalOpen = isConsentModalOpen || isCookieSettingsModalOpen;

  if (!isModalOpen) {
    return null;
  }

  return (
      <Modal
        isOpen={isModalOpen}
        onClose={isCookieSettingsModalOpen ? closePreferences : () => {}}
        width="960px"
        closeButtonProps={{
          hidden: !isCookieSettingsModalOpen
        }}
      >
        <Flex
          flexDirection="column"
          gap={4}
          padding={8}
          role="region"
          aria-label={
            isCookieSettingsModalOpen
              ? 'Cookie settings'
              : 'Cookie consent banner'
          }
        >
          <Flex flexDirection="column" gap={2}>
            <Text
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="700"
              mb={2}
            >
              {isCookieSettingsModalOpen
                ? 'Cookie Settings'
                : 'We use cookies on Journal Manager'}
            </Text>
            <Text color="gray.700">
              We use cookies and similar tracking technologies to improve your
              experience on Journal Manager and analyse site usage. Some
              cookies are essential for Journal Manager to function, while
              others help us understand how you interact with our content.
            </Text>
            <Text color="gray.700">
              You can choose to accept all cookies, reject non-essential ones,
              or customize your preferences. For more details, see our{' '}
              <Link href={COOKIE_POLICY_URL} isExternal variant="primary">
                Cookie Policy
              </Link>{' '}
              and{' '}
              <Link href={PRIVACY_POLICY_URL} isExternal variant="primary">
                Privacy Policy
              </Link>
              .
            </Text>
          </Flex>

          <OutlineCard padding={5} width="100%">
            <Flex justify="space-between" gap={4} align="center">
              <Box>
                <Text fontWeight="700">Necessary cookies</Text>
                <Text color="gray.700">
                  Required for login, forms, and peer review workflows.
                </Text>
              </Box>
              <Badge variant="success">Always active</Badge>
            </Flex>
          </OutlineCard>

          <OutlineCard padding={5} width="100%">
            <Flex justify="space-between" gap={4} align="center">
              <Box>
                <Text fontWeight="700">Analytics cookies</Text>
                <Text color="gray.700">
                  Google Analytics, pages visited, access times, and device
                  information.
                </Text>
              </Box>
              <Switch
                isChecked={draftPreferences.analytics}
                onChange={event =>
                  setDraftPreferences(current => ({
                    ...current,
                    analytics: event.target.checked
                  }))
                }
              />
            </Flex>
          </OutlineCard>

          <Flex gap={3} direction={{ base: 'column', md: 'row' }}>
            <Button
              variant="secondary"
              onClick={() => savePreferences(draftPreferences)}
            >
              Save Preferences
            </Button>
            <Button variant="secondary" onClick={rejectAll}>
              Reject Non-Essential
            </Button>
            <Button variant="primary" onClick={acceptAll}>
              Accept All
            </Button>
          </Flex>

          <Text fontSize="sm" color="gray.600">
            You can withdraw your consent at any time by contacting us at{' '}
            <Link
              href={`mailto:${contactSupportMail}`}
              isExternal
              variant="primary"
            >
              {contactSupportMail}
            </Link>{' '}
            or using the Cookie Settings link in the footer.
          </Text>
        </Flex>
      </Modal>
  );
};

export default CookieConsentBanner;
